import React from 'react';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';

import { useRouteConfiguration } from '../../../../context/routeConfigurationContext.js';
import { matchPathname } from '../../../../util/routes.js';

import { NamedLink, ExternalLink } from '../../../../components/index.js';
import css from './Link.module.css';

/**
 * Link element which internally uses NamedLink or ExternalLink
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {ReactNode} props.children
 * @param {string} props.href link destination. In-app links need to start with '/'.
 * @returns {JSX.Element} link element
 */
/**
 * Sprungmarken innerhalb derselben Seite (Inhaltsverzeichnisse der Rechtsseiten).
 * React Router schreibt bei einer Client-Navigation nur den Hash in die URL — die
 * native Sprungmarken-Navigation des Browsers greift dabei nicht, die Seite bliebe
 * also stehen. Deshalb scrollen wir selbst; `scroll-margin-top` der Sections sorgt
 * dafür, dass die sticky Topbar die Überschrift nicht verdeckt.
 *
 * @param {string} hash z. B. '#privacy-doc-rechte'
 * @returns {Function|undefined} Click-Handler oder undefined, wenn kein Hash vorliegt
 */
const scrollToHashHandler = hash => {
  const id = hash && hash.charAt(0) === '#' ? hash.slice(1) : null;
  if (!id) {
    return undefined;
  }

  return event => {
    // Modifizierte Klicks (neuer Tab/Fenster) dem Browser überlassen.
    const isModified =
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey;
    if (isModified || typeof document === 'undefined') {
      return;
    }

    const target = document.getElementById(id);
    if (!target || typeof target.scrollIntoView !== 'function') {
      return;
    }
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    target.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
  };
};

export const Link = React.forwardRef((props, ref) => {
  const location = useLocation();
  const routes = useRouteConfiguration();

  const { className, rootClassName, href, title, children } = props;
  const classes = classNames(rootClassName || css.link, className);
  const titleMaybe = title ? { title } : {};
  const linkProps = { className: classes, href, children, ...titleMaybe };

  // Markdown parser (rehype-sanitize) might return undefined href
  if (!href || !children) {
    return null;
  }

  if (href.charAt(0) === '/') {
    // Internal link
    const testURL = new URL('http://my.marketplace.com' + href);
    const matchedRoutes = matchPathname(testURL.pathname, routes);
    if (matchedRoutes.length > 0) {
      const found = matchedRoutes[0];
      const to = { search: testURL.search, hash: testURL.hash };
      // Nur wenn das Ziel dieselbe Seite ist — sonst lädt die neue Seite ohnehin
      // frisch und der Browser springt selbst an die Marke.
      const isSamePage = testURL.pathname === location.pathname;
      const onClickMaybe =
        isSamePage && testURL.hash ? { onClick: scrollToHashHandler(testURL.hash) } : {};
      return (
        <NamedLink
          name={found.route.name}
          params={found.params}
          to={to}
          {...linkProps}
          {...onClickMaybe}
          ref={ref}
        />
      );
    }
  }

  if (href.charAt(0) === '#') {
    if (typeof window !== 'undefined') {
      const hash = href;
      let testURL = new URL(
        `http://my.marketplace.com${location.pathname}${location.hash}${location.search}`
      );
      testURL.hash = hash;
      const matchedRoutes = matchPathname(testURL.pathname, routes);
      if (matchedRoutes.length > 0) {
        const found = matchedRoutes[0];
        const to = { search: testURL.search, hash: testURL.hash };
        return (
          <NamedLink
            name={found.route.name}
            params={found.params}
            to={to}
            {...linkProps}
            onClick={scrollToHashHandler(testURL.hash)}
            ref={ref}
          />
        );
      }
    }
  }

  return <ExternalLink {...linkProps} ref={ref} />;
});

Link.displayName = 'Link';
